import { useMessages } from '@hooks/useMessages';
import React, { useEffect, useState } from 'react';
import { DropdownItem } from 'reactstrap';
import {
  ReservationItem,
  ReservationItemProps,
} from '../../molecules/reservation-item';
import reservationsApi from '@src/@data/api/reservations';
import { CalendarType } from '@src/@data/types/calendars';
import { ClientTimeReservationDetailsResponse } from '@src/@data/types/reservations';
import { formatName } from '@src/@data/utility/names';
import { useFormatters } from '@hooks/useFormatters';
import { logOnError } from '@src/@data/utility/errors';
import QrScannerBackButton from '@src/components/organisms/qr-scanner/QrScannerBackButton';

interface QrScannerReservationItemProps {
  companyId: string;
  reservationId: string;
  onBack: () => void;
}

export const QrScannerReservationItem: React.FC<QrScannerReservationItemProps> =
  ({ companyId, reservationId, onBack }) => {
    const { messages } = useMessages();
    const { moneyFormatter } = useFormatters();
    const [isLoading, setIsLoading] = useState(false);
    const [reservation, setReservation] = useState<
      ReservationItemProps['reservation'] & {
        clientName?: string;
        spotsText?: string;
        price: string;
      }
    >();
    const [error, setError] = useState<Error>();

    useEffect(() => {
      reservationsApi
        .getTimeReservationDetails(companyId, reservationId)
        .then((res) => {
          const {
            id,
            service,
            startDate,
            endDate,
            client,
            price,
            currency,
            type,
            slotData,
            timeZone,
          } = res as ClientTimeReservationDetailsResponse;
          setReservation({
            type,
            id,
            serviceTitle: service.title,
            startDate,
            timeZone,
            endDate,
            status: res.status,
            clientName: formatName(client.firstName, client.lastName),
            price: moneyFormatter.format(price, { fromCents: true, currency }),
            spotsText: slotData
              ? `${slotData.spots}/${slotData.slot.spotCount}`
              : undefined,
            slotId: slotData?.slot.id,
            shared: false,
          });
        })
        .catch((err) => {
          logOnError(err);
          setError(err);
          setReservation(undefined);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, [companyId, moneyFormatter, reservationId]);

    return (
      <>
        {!isLoading && (!!reservation || !!error) && (
          <>
            <QrScannerBackButton
              message={
                reservation
                  ? messages('qr_scanner_dropdown_reservation_found')
                  : messages('qr_scanner_dropdown_reservation_not_found')
              }
              onBack={onBack}
            />
            {!!reservation && (
              <>
                <DropdownItem divider />
                <DropdownItem style={{ width: '100%' }}>
                  <ReservationItem
                    reservation={reservation}
                    companyId={companyId}
                    spotInfo={
                      reservation.type === CalendarType.Time
                        ? reservation.clientName
                        : reservation.spotsText
                    }
                    price={reservation.price}
                  />
                </DropdownItem>
              </>
            )}
          </>
        )}
      </>
    );
  };

export default QrScannerReservationItem;
